import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import { VenueFormData } from '../../../types/Venue';

interface VenueContactInfoFormProps {
  venueData: VenueFormData;
  updateVenueData: (data: Partial<VenueFormData>) => void;
}

const VenueContactInfoForm: React.FC<VenueContactInfoFormProps> = ({ venueData, updateVenueData }) => {
  const handleContactChange = (field: keyof typeof venueData.contact, value: string) => {
    updateVenueData({
      contact: {
        ...venueData.contact,
        [field]: value
      }
    });
  };

  const handleSocialChange = (field: keyof NonNullable<typeof venueData.socialMedia>, value: string) => {
    updateVenueData({
      socialMedia: {
        ...venueData.socialMedia,
        [field]: value
      }
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Contact Information
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Email Address"
            value={venueData.contact.email}
            onChange={(e) => handleContactChange('email', e.target.value)}
            helperText="Business email for customer inquiries"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Phone Number"
            value={venueData.contact.phone}
            onChange={(e) => handleContactChange('phone', e.target.value)}
            helperText="Business phone number with area code"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Website"
            value={venueData.contact.website || ''}
            onChange={(e) => handleContactChange('website', e.target.value)}
            helperText="Your venue's official website (if available)"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <WebsiteIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 4 }} />
      
      <Typography variant="h6" gutterBottom>
        Social Media (Optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Add your venue's social media profiles to enhance discoverability and drive engagement
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Facebook"
            value={venueData.socialMedia?.facebook || ''}
            onChange={(e) => handleSocialChange('facebook', e.target.value)}
            helperText="Facebook page URL"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FacebookIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Instagram"
            value={venueData.socialMedia?.instagram || ''}
            onChange={(e) => handleSocialChange('instagram', e.target.value)}
            helperText="Instagram profile URL"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InstagramIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Twitter"
            value={venueData.socialMedia?.twitter || ''}
            onChange={(e) => handleSocialChange('twitter', e.target.value)}
            helperText="Twitter profile URL"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TwitterIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="LinkedIn"
            value={venueData.socialMedia?.linkedin || ''}
            onChange={(e) => handleSocialChange('linkedin', e.target.value)}
            helperText="LinkedIn profile URL"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkedInIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default VenueContactInfoForm;
